import { Country, Language } from '../config/types';

/**
 * Defines which languages are available for each country.
 * This mapping is based on existing translation files and business rules.
 *
 * Translation files available:
 * - English: en-ee.json, en-ke.json, en-lv.json, en-za.json
 * - Estonian: et-ee.json
 * - Latvian: lv-lv.json
 */
const COUNTRY_LANGUAGE_MAP: Record<Country, Language[]> = {
  [Country.EE]: [Language.et, Language.en], // Estonia: Estonian, English
  [Country.LV]: [Language.lv, Language.en], // Latvia: Latvian, English
  [Country.KE]: [Language.en], // Kenya: English only
  [Country.ZA]: [Language.en], // South Africa: English only
  [Country.EG]: [Language.en], // Egypt: English only
};

/**
 * Default language for each country (first in the list)
 */
const COUNTRY_DEFAULT_LANGUAGE_MAP: Record<Country, Language> = {
  [Country.EE]: Language.et,
  [Country.LV]: Language.lv,
  [Country.KE]: Language.en,
  [Country.ZA]: Language.en,
  [Country.EG]: Language.en,
};

/**
 * Gets the default language for a specific country
 * @param country - The country code
 * @returns Default language for that country
 */
export const getDefaultLanguageForCountry = (country: Country): Language => {
  const defaultLanguage = COUNTRY_DEFAULT_LANGUAGE_MAP[country];

  if (!defaultLanguage) {
    console.warn(`No default language found for country: ${country}. Falling back to English.`);

    return Language.en;
  }

  return defaultLanguage;
};

/**
 * Gets available languages for a specific country
 * @param country - The country code
 * @returns Array of available languages for that country
 */
export const getAvailableLanguagesForCountry = (country: Country): Language[] => {
  const languages = COUNTRY_LANGUAGE_MAP[country];

  if (!languages) {
    console.warn(`No language mapping found for country: ${country}. Falling back to English.`);

    return [getDefaultLanguageForCountry(country)];
  }

  return languages;
};

/**
 * Validates if a language is available for a specific country
 * @param language - The language to check
 * @param country - The country to check against
 * @returns True if language is available for the country
 */
export const isLanguageAvailableForCountry = (language: Language, country: Country): boolean => {
  const availableLanguages = getAvailableLanguagesForCountry(country);

  return availableLanguages.includes(language);
};

/**
 * Gets all countries where a specific language is available
 * @param language - The language to check
 * @returns Array of countries where the language is available
 */
export const getCountriesForLanguage = (language: Language): Country[] => {
  return Object.entries(COUNTRY_LANGUAGE_MAP)
    .filter(([, languages]) => languages.includes(language))
    .map(([country]) => country as Country);
};

/**
 * Type guard to ensure a country is valid
 * @param country - The country to validate
 * @returns True if country is a valid Country enum value
 */
export const isValidCountry = (country: string): country is Country => {
  return Object.values(Country).includes(country as Country);
};

/**
 * Type guard to ensure a language is valid
 * @param language - The language to validate
 * @returns True if language is a valid Language enum value
 */
export const isValidLanguage = (language: string): language is Language => {
  return Object.values(Language).includes(language as Language);
};
